import { Chart as ChartJS, Filler, LineElement, PointElement, RadialLinearScale, Tooltip } from 'chart.js'
import { FC, useMemo } from 'react'
import { Radar } from 'react-chartjs-2'

import { alpha, Box, Paper, Typography, useTheme } from '@mui/material'

import { useRadarChartOptions } from '../../helpers/chartTheme'
import { getMotivationChartHeight } from '../../helpers/motivationChartHeight'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { MotivationScore } from '../../types'

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip)

interface ComparisonGame {
  name: string
  /** Game-design (Game & Live Events) motivation scores for this game, or `null` when unavailable. */
  scores: MotivationScore[] | null
}

interface ComparisonRadarChartProps {
  source: ComparisonGame
  target: ComparisonGame
  /** Copy used for the disclaimer rendered when either game lacks `scores`. */
  unavailableMessage: string
}

/**
 * Two-polygon radar chart rendering each game's `gameDesign` (Game & Live
 * Events) motivation profile so the user can read the visual overlap between
 * the two games at a glance.
 *
 * Mirrors the styling decisions of [`MotivationRadarChart`](../MotivationRadarChart/MotivationRadarChart.tsx)
 * (Chart.js radar + transparent fills + tooltip with circular swatches) and
 * reuses the same source-vs-creatives palette (`secondary.main` for the
 * source game, matching the page's `gameDesign` series, and `primary.main`
 * for the target, matching the page's `creativeAds` series) so users moving
 * between the single-game page and the comparison dialog see a familiar
 * colour vocabulary even though the datasets here represent two GAMES rather
 * than two motivation sources.
 *
 * When either game's `scores` is `null` (no in-game profile on file) we
 * render a single disclaimer card instead of an empty/zeroed polygon. The
 * single-game radar applies the same "do not lie about missing data" rule
 * by dropping unavailable series; here we drop the entire chart because a
 * comparison only makes sense with two profiles.
 */
export const ComparisonRadarChart: FC<ComparisonRadarChartProps> = ({ source, target, unavailableMessage }) => {
  const theme = useTheme()
  const { motivationLabel } = useUATaxonomy()

  const sourceColor = theme.palette.secondary.main
  const targetColor = theme.palette.primary.main

  const labels = useMemo(() => {
    const ref = source.scores ?? target.scores ?? []
    return ref.map((s) => motivationLabel(s.motivation))
  }, [source.scores, target.scores, motivationLabel])

  const chartData = useMemo(() => {
    if (!source.scores || !target.scores) return null
    return {
      labels,
      datasets: [
        {
          label: source.name,
          data: source.scores.map((s) => s.avgIntensity),
          backgroundColor: alpha(sourceColor, 0.16),
          borderColor: sourceColor,
          pointBackgroundColor: sourceColor,
          pointBorderColor: sourceColor,
          pointBorderWidth: 0,
          pointHoverBackgroundColor: sourceColor,
          pointHoverBorderColor: sourceColor,
          pointHoverBorderWidth: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
          pointRadius: 2,
          pointStyle: 'circle' as const,
          fill: true,
        },
        {
          label: target.name,
          data: target.scores.map((s) => s.avgIntensity),
          backgroundColor: alpha(targetColor, 0.16),
          borderColor: targetColor,
          pointBackgroundColor: targetColor,
          pointBorderColor: targetColor,
          pointBorderWidth: 0,
          pointHoverBackgroundColor: targetColor,
          pointHoverBorderColor: targetColor,
          pointHoverBorderWidth: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
          pointRadius: 2,
          pointStyle: 'circle' as const,
          fill: true,
        },
      ],
    }
  }, [labels, source, target, sourceColor, targetColor])

  const options = useRadarChartOptions()

  if (!chartData) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2.25,
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 120,
          backgroundColor: alpha(theme.palette.warning.main, 0.06),
          borderColor: alpha(theme.palette.warning.main, 0.3),
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.primary', textAlign: 'center', maxWidth: 520 }}>
          {unavailableMessage}
        </Typography>
      </Paper>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ height: getMotivationChartHeight(labels.length), position: 'relative' }}>
        <Radar data={chartData} options={options} />
      </Box>
      <Box
        role="list"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: 2, sm: 3.5 },
        }}
      >
        <LegendSwatch color={sourceColor} label={source.name} />
        <LegendSwatch color={targetColor} label={target.name} />
      </Box>
    </Box>
  )
}

interface LegendSwatchProps {
  color: string
  label: string
}

/**
 * Inline legend entry for the comparison radar. The shared
 * `MotivationSourceLegend` is keyed off the source palette and would label
 * the games as "Game & Live Events" / "Creative Ads", so we render a small
 * non-interactive legend here keyed off game names instead.
 */
const LegendSwatch: FC<LegendSwatchProps> = ({ color, label }) => (
  <Box role="listitem" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
    <Box aria-hidden sx={{ width: 11, height: 11, borderRadius: '50%', backgroundColor: color }} />
    <Typography variant="body2" sx={{ fontSize: 13, color: 'text.primary' }}>
      {label}
    </Typography>
  </Box>
)
